import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { colors, constants } from "utils";
import { logError, LogCategory } from "utils/sentryLogger";
import checkIcon from "assets/icons/live/check.svg";
import noLiveSessionsIcon from "assets/icons/live/no-live-sessions.svg";
import { BetaTag } from "components/shared/BetaTag/BetaTag";
import { BetaBanner } from "components/shared/BetaBanner/BetaBanner";
import { SelectDropdown } from "components/shared/FilterDropdowns/FilterDropdowns";
import { useLiveSessions } from "hooks/useLiveSessionsStream/LiveSessionsProvider";
import { getReportClassList } from "hooks/useFilterPersistence/useFilterPersistence";
import { LiveSessionsSection } from "./LiveSessionsSection";
import { groupCodeLabel } from "./LiveSessionPill";

export const LiveSessionsHeader = () => (
  <HeaderRow>
    {constants.LIVE_SESSIONS} <BetaTag />
  </HeaderRow>
);

const ALL_CLASSES = "All Classes";

// Unique per class, like the group-code fallback, so two live classes stay
// apart when names are missing (the list failed, or it doesn't carry them).
const classFallbackName = (id) => `Class …${String(id).slice(-5).toUpperCase()}`;

// The report for a session, with the safety-alert link's tab and quote.
const reportLink = ({ sessionId, reportTab, quoteId }) => {
  const params = new URLSearchParams({ session_id: sessionId, date_range: "all_time" });
  if (reportTab) params.set("report_tab", reportTab);
  if (quoteId) params.set("quote_id", quoteId);
  return `${constants.SESSION_HISTORY_PATH}?${params}`;
};

// The live dashboard: every session running right now that this teacher's
// live feed carries, narrowed by class and by group code.
export const LiveSessionsPage = () => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const live = useLiveSessions();

  // A safety-alert link opened mid-session lands here with the pill to open.
  const [alertTarget] = useState(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    return sessionId
      ? { sessionId, reportTab: params.get("report_tab"), quoteId: params.get("quote_id") }
      : null;
  });
  // Once the feed has answered (and isn't in an outage), a session it doesn't
  // carry has ended or isn't this teacher's to watch: point at its report
  // rather than an empty page.
  const alertSessionGone =
    alertTarget &&
    live.snapshotCount > 0 &&
    !live.outage &&
    !live.sessions?.[alertTarget.sessionId];

  const [classes, setClasses] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getReportClassList(queryClient, null)
      .then((list) => {
        if (!cancelled) setClasses(list || []);
      })
      .catch((e) =>
        // Names only label the filter and pills; the sessions still show.
        logError("LiveSessions: class list failed to load", e, { category: LogCategory.API }),
      );
    return () => {
      cancelled = true;
    };
  }, [queryClient]);

  const sessions = useMemo(() => Object.values(live.sessions || {}), [live.sessions]);

  const liveClasses = useMemo(() => {
    const nameById = new Map(classes.map((c) => [c.id, c.name]));
    // An early event can lack classroom_id; those show under every pick.
    const ids = [...new Set(sessions.map((s) => s.classroom_id).filter(Boolean))];
    return ids
      .map((id) => ({ id, name: nameById.get(id) || classFallbackName(id) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [sessions, classes]);

  // With one live class it is forced; otherwise a pick whose class has no
  // live session left falls back to All Classes.
  const [pickedClassId, setPickedClassId] = useState(null);
  const classId =
    liveClasses.length === 1
      ? liveClasses[0].id
      : liveClasses.some((c) => c.id === pickedClassId)
        ? pickedClassId
        : null;
  const inClass = sessions.filter(
    (s) => classId == null || !s.classroom_id || s.classroom_id === classId,
  );

  // Track deselected chips rather than selected ones, so a session that
  // starts later shows by default.
  const [hiddenIds, setHiddenIds] = useState(() => new Set());
  const toggleHidden = (sessionId) =>
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  const shown = Object.fromEntries(
    inClass.filter((s) => !hiddenIds.has(s.session_id)).map((s) => [s.session_id, s]),
  );

  const classOptions = [
    { value: null, label: ALL_CLASSES, pinned: true },
    ...liveClasses.map((c) => ({ value: c.id, label: c.name })),
  ];

  return (
    <Page>
      <BetaBanner
        title="Reports Updated"
        text={
          <>
            We've improved how this report is calculated, so some data may look
            different than before. If you'd like access to the previous version,
            please contact{" "}
            <a href="mailto:support@okolabs.ai">support@okolabs.ai</a>
          </>
        }
      />
      {alertSessionGone && (
        <Notice role="status">
          The session from your safety alert isn't running anymore.{" "}
          <Link to={reportLink(alertTarget)}>View its report</Link>
        </Notice>
      )}
      {sessions.length === 0 ? (
        <EmptyByStatus status={live.status} outage={live.outage} answered={live.snapshotCount > 0} />
      ) : (
        <>
          {live.outage && (
            <Notice role="status">
              Live updates are paused while we reconnect, so what you see may be out of date.
            </Notice>
          )}
          <Filters>
            {liveClasses.length > 1 ? (
              <SelectDropdown
                label="Class"
                value={classId}
                displayText={liveClasses.find((c) => c.id === classId)?.name || ALL_CLASSES}
                options={classOptions}
                onChange={setPickedClassId}
              />
            ) : (
              <SingleClass>
                <span>Class:</span> {liveClasses[0]?.name}
              </SingleClass>
            )}
            <Chips role="group" aria-label="Group codes">
              <ChipsLabel>Group Codes:</ChipsLabel>
              {inClass.map((s) => {
                const selected = !hiddenIds.has(s.session_id);
                return (
                  <Chip
                    key={s.session_id}
                    type="button"
                    aria-pressed={selected}
                    $selected={selected}
                    onClick={() => toggleHidden(s.session_id)}
                  >
                    {selected && <img src={checkIcon} alt="" aria-hidden="true" />}
                    {groupCodeLabel(s)}
                  </Chip>
                );
              })}
            </Chips>
          </Filters>
          <LiveSessionsSection
            status={live.status}
            sessions={shown}
            transcripts={live.transcripts}
            snapshotCount={live.snapshotCount}
            classes={classes}
            initialExpandedId={alertTarget?.sessionId ?? null}
            onEndSession={(s) => live.endSession(s.session_id)}
          />
        </>
      )}
    </Page>
  );
};

// Nothing to show: say why. Only a feed that has answered, and isn't in an
// outage, can say nothing is running; a failed or switched-off one must not
// read as "no sessions".
const EmptyByStatus = ({ status, outage, answered }) => {
  if (status === "disabled") {
    return (
      <Empty>
        <EmptyTitle>Live Sessions Aren't Turned On</EmptyTitle>
        <EmptyText>Live sessions aren't available for your account yet.</EmptyText>
      </Empty>
    );
  }
  if (outage) {
    return (
      <Empty role="status">
        <EmptyTitle>We Can't Check for Live Sessions Right Now</EmptyTitle>
        <EmptyText>
          We're reconnecting automatically. Safety alerts are still sent to you by email.
        </EmptyText>
      </Empty>
    );
  }
  if (answered) {
    return (
      <Empty>
        <img src={noLiveSessionsIcon} alt="" aria-hidden="true" />
        <EmptyTitle>No Live Sessions Right Now</EmptyTitle>
        <EmptyText>
          There are no active sessions at the moment. Once a session starts, it will appear here.
        </EmptyText>
      </Empty>
    );
  }
  return (
    <Empty role="status">
      <EmptyText>Checking for live sessions…</EmptyText>
    </Empty>
  );
};

const Notice = styled.p`
  margin: 0 0 24px;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid ${colors.blue300};
  background: #f4f4fd;
  font-family: Inter, sans-serif;
  font-size: 16px;
  line-height: 22px;
  color: #002e19;

  a {
    color: ${colors.blue700};
    text-decoration: underline;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// The banner brings its own margins, as on Session History.
const Page = styled.div`
  display: flex;
  flex-direction: column;
`;

const Filters = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 24px;
`;

const SingleClass = styled.p`
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #002e19;

  span {
    font-size: 12px;
    color: ${colors.nightlyWoods500};
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
`;

const ChipsLabel = styled.span`
  margin-right: -8px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #002e19;
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: ${({ $selected }) => ($selected ? "4px 16px 4px 8px" : "4px 16px")};
  border-radius: 9999px;
  border: 1px solid ${({ $selected }) => ($selected ? colors.blue500 : "#BAB9F5")};
  background: ${({ $selected }) => ($selected ? colors.blue500 : "#fff")};
  color: ${({ $selected }) => ($selected ? "#fff" : "#524FC9")};
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.25px;
  cursor: pointer;

  img {
    width: 24px;
    height: 24px;
  }

  &:focus-visible {
    outline: 2px solid ${colors.blue800};
    outline-offset: 2px;
  }
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;

  img {
    width: 60px;
    height: 60px;
  }
`;

const EmptyTitle = styled.h2`
  margin: 0;
  font-family: "ABC Marfa", sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  color: #000;
`;

const EmptyText = styled.p`
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: #000;
`;
